import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  PlayCircle, Pencil, Pulse, Sparkle, CheckCircle, X, Spinner, ChatCircleText,
  Plus, ArrowCounterClockwise, Hourglass, Lightning, Star, Engine, ArrowUUpLeft,
} from "@phosphor-icons/react";

import Topbar from "@/components/Topbar";
import { api } from "@/lib/api";
import VariableInserter from "@/components/VariableInserter";
import { useSector } from "@/lib/sectorContext";

const RULE_VARS = [
  { token: "{nome_cliente}", label: "Primeiro nome do cliente" },
  { token: "{veiculo}", label: "Veículo/produto do cliente" },
  { token: "{nome_empresa}", label: "Nome da sua empresa" },
];

// Icon lookup for template cards — backend sends an icon name string,
// frontend maps it to a phosphor component.
const ICON_MAP = {
  ArrowCounterClockwise, Hourglass, Lightning, Star, Engine, ArrowUUpLeft,
};

const CATEGORY_LABELS = {
  reativacao: "Reativação",
  pos_atendimento: "Pós-atendimento",
  lembrete: "Lembrete",
};

// ───────────────────────────────────────────────────────────────────────
// Page: Automações Inteligentes
//
// Replaces the technical "Decision Engine" surface with a calm, operator-
// friendly view. The rule's internal `code` is NEVER shown — instead we
// use the human strings `when_human` / `what_human` computed server-side
// by `engines/decision_engine.display_for`.
//
// Visual primitives kept very minimal (slate/white/emerald) to match the
// rest of the SaaS theme. No purple gradients, no over-decoration.
// ───────────────────────────────────────────────────────────────────────

export default function Decision() {
  const { enablesKm } = useSector();
  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);    // result modal
  const [editing, setEditing] = useState(null);        // rule being edited
  const [creating, setCreating] = useState(false);     // create-new-rule modal open
  const [activatingId, setActivatingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get("/decision/rules"),
        api.get("/decision/logs?limit=15"),
        api.get("/decision/templates"),
      ]);
      setRules(r1.data);
      setLogs(r2.data);
      setTemplates(r3.data);
    } catch {
      toast.error("Não consegui carregar as automações.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleToggle = async (rule) => {
    try {
      await api.patch(`/decision/rules/${rule.id}/toggle`);
      setRules((prev) => prev.map((r) => r.id === rule.id ? { ...r, active: !r.active } : r));
    } catch {
      toast.error("Não foi possível alterar essa automação.");
    }
  };

  const handleActivateTemplate = async (tpl) => {
    setActivatingId(tpl.id);
    try {
      await api.post(`/decision/templates/${tpl.id}/activate`);
      toast.success(`"${tpl.rule_seed.label}" agora está ativa.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não consegui ativar essa automação.");
    } finally {
      setActivatingId(null);
    }
  };

  const handleRun = async () => {
    setRunning(true);
    setRunResult(null);
    try {
      const { data } = await api.post("/decision/run");
      setRunResult(data);
      await load();
    } catch {
      toast.error("Erro ao executar.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <Topbar
        title="Automações Inteligentes"
        subtitle="O AgentUS acompanha seus clientes e executa ações automáticas para aumentar retornos, recorrência e relacionamento."
        actions={
          <button
            onClick={handleRun}
            disabled={running}
            data-testid="run-automations-btn"
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed transition-colors text-[12px] font-medium shadow-sm"
          >
            {running ? (
              <><Spinner size={13} weight="regular" className="animate-spin" /> Executando…</>
            ) : (
              <><PlayCircle size={13} weight="fill" /> Executar agora</>
            )}
          </button>
        }
      />

      <div className="flex-1 overflow-auto p-4 md:p-6 space-y-6">
        {/* ─── Templates 1-clique ─── */}
        <section
          className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-xl overflow-hidden"
          data-testid="rule-templates-section"
        >
          <header className="px-5 py-3 border-b border-slate-200 flex flex-wrap items-center gap-2">
            <Plus size={14} weight="bold" className="text-slate-700" />
            <h2 className="text-[13px] font-semibold tracking-tight text-slate-900">
              Modelos prontos
            </h2>
            <span className="text-[11px] text-slate-500">
              · adicione com 1 clique nas suas automações
            </span>
            <button
              type="button"
              onClick={() => setCreating(true)}
              data-testid="create-custom-automation-btn"
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-900 text-white hover:bg-slate-800 transition-colors text-[11.5px] font-medium shadow-sm"
            >
              <Plus size={11} weight="bold" /> Criar do zero
            </button>
          </header>
          {(() => {
            const available = templates.filter((t) => !t.already_active);
            if (available.length === 0) {
              return (
                <div className="p-8 text-center" data-testid="templates-empty-state">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 mb-2">
                    <CheckCircle size={18} weight="fill" />
                  </div>
                  <p className="text-[13px] text-slate-700 font-medium">Você já adicionou todos os modelos prontos.</p>
                  <p className="text-[12px] text-slate-500 mt-1">
                    Continue ajustando suas automações abaixo, ou crie uma do zero pra atender uma necessidade específica.
                  </p>
                </div>
              );
            }
            return (
              <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {available.map((tpl) => {
                  const Icon = ICON_MAP[tpl.icon] || Sparkle;
                  return (
                    <article
                      key={tpl.id}
                      data-testid={`template-card-${tpl.id}`}
                      className="relative rounded-lg border bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm p-4 transition-all flex flex-col gap-2"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="w-8 h-8 rounded-md flex items-center justify-center bg-slate-100 text-slate-700">
                          <Icon size={16} weight="regular" />
                        </span>
                        <span className="text-[9px] uppercase tracking-[0.14em] text-slate-400 font-medium">
                          {CATEGORY_LABELS[tpl.category] || "Automação"}
                        </span>
                      </div>
                      <h3 className="text-[13.5px] font-semibold text-slate-900 tracking-tight leading-snug">
                        {tpl.tagline}
                      </h3>
                      <p className="text-[12px] text-slate-500 leading-relaxed flex-1">
                        {tpl.description}
                      </p>
                      <p className="text-[11px] text-slate-500/90 italic leading-relaxed" data-testid={`template-edit-hint-${tpl.id}`}>
                        Você pode personalizar a mensagem depois de ativar — é só clicar no <Pencil size={10} weight="bold" className="inline -mt-0.5" /> ao lado.
                      </p>
                      <button
                        onClick={() => handleActivateTemplate(tpl)}
                        disabled={activatingId === tpl.id}
                        data-testid={`template-activate-${tpl.id}`}
                        className="mt-2 inline-flex items-center justify-center gap-1.5 bg-slate-900 text-white py-1.5 rounded-md text-[12px] font-medium hover:bg-slate-700 transition-colors disabled:opacity-60"
                      >
                        {activatingId === tpl.id ? (
                          <><Spinner size={11} className="animate-spin" /> Adicionando…</>
                        ) : (
                          <><Plus size={11} weight="bold" /> Adicionar às minhas automações</>
                        )}
                      </button>
                    </article>
                  );
                })}
              </div>
            );
          })()}
        </section>

        {/* ─── Automações ativas ─── */}
        <section className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="automations-section">
          <header className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
            <Sparkle size={14} weight="fill" className="text-amber-500" />
            <h2 className="text-[13px] font-semibold tracking-tight text-slate-900">Suas automações</h2>
            <span className="ml-auto text-[11px] text-slate-500 tabular-nums">
              {rules.filter((r) => r.active).length} de {rules.length} ativas
            </span>
          </header>
          <div className="px-5 py-2.5 bg-slate-50 border-b border-slate-100 text-[12px] text-slate-600 leading-relaxed" data-testid="automations-opt-in-hint">
            Aqui ficam todas as automações que você ativou — tanto as que vieram dos <strong className="text-slate-800">Modelos prontos</strong> quanto as que você criou do zero.
            Use o botão <span className="font-medium text-slate-800">ligar/desligar</span> à direita pra ativar ou pausar uma automação,
            e o lápis <Pencil size={11} weight="bold" className="inline -mt-0.5" /> pra personalizar a mensagem do seu jeito a qualquer momento.
          </div>

          {loading ? (
            <div className="p-8 text-center text-sm text-slate-400">Carregando…</div>
          ) : rules.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-slate-700 font-medium text-sm mb-1">Nenhuma automação configurada</div>
              <div className="text-xs text-slate-500">Aguarde — assim que carregar pela primeira vez, as automações padrão são criadas.</div>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {rules.map((rule) => (
                <li key={rule.id} className="px-5 py-4 grid grid-cols-12 items-center gap-3 hover:bg-slate-50/50 transition-colors" data-testid={`automation-row-${rule.code}`}>
                  {/* Automação (label) */}
                  <div className="col-span-12 md:col-span-3">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400 mb-0.5 md:hidden">Automação</div>
                    <div className="text-[14px] font-medium text-slate-900 tracking-tight">
                      {rule.label}
                    </div>
                  </div>
                  {/* Quando acontece */}
                  <div className="col-span-6 md:col-span-3 text-[13px] text-slate-600">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400 mb-0.5 md:hidden">Quando acontece</div>
                    {rule.when_human}
                  </div>
                  {/* O que o AgentUS faz */}
                  <div className="col-span-6 md:col-span-4 text-[13px] text-slate-600">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400 mb-0.5 md:hidden">O que o AgentUS faz</div>
                    {rule.what_human}
                  </div>
                  {/* Toggle + edit */}
                  <div className="col-span-12 md:col-span-2 flex items-center justify-end gap-3">
                    <button
                      onClick={() => handleToggle(rule)}
                      data-testid={`toggle-${rule.code}`}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${rule.active ? "bg-emerald-500" : "bg-slate-300"}`}
                      aria-label={rule.active ? "Desativar" : "Ativar"}
                    >
                      <span
                        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${rule.active ? "translate-x-5" : "translate-x-1"}`}
                      />
                    </button>
                    <button
                      onClick={() => setEditing(rule)}
                      data-testid={`edit-${rule.code}`}
                      className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                      aria-label="Editar"
                    >
                      <Pencil size={14} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* ─── Ações recentes do AgentUS ─── */}
        <section className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="recent-actions-section">
          <header className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
            <Pulse size={14} weight="fill" className="text-emerald-500" />
            <h2 className="text-[13px] font-semibold tracking-tight text-slate-900">Ações recentes do AgentUS</h2>
            <span className="ml-auto text-[11px] text-slate-500">últimas 15</span>
          </header>
          {logs.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-slate-700 font-medium text-sm mb-1">Nenhuma ação automática ainda</div>
              <div className="text-xs text-slate-500">Clique em <span className="font-medium text-slate-700">"Executar agora"</span> pra rodar o ciclo e ver o AgentUS em ação.</div>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {logs.map((log) => (
                <li key={log.id} className="px-5 py-3 flex items-start gap-3" data-testid={`action-log-${log.id}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                    log.decision === "acted" ? "bg-emerald-50 text-emerald-700" :
                    log.decision === "cooldown" ? "bg-amber-50 text-amber-700" :
                    "bg-slate-50 text-slate-400"
                  }`}>
                    {log.decision === "acted" ? <ChatCircleText size={13} weight="fill" /> :
                     log.decision === "cooldown" ? <Spinner size={13} weight="regular" /> :
                     <X size={13} weight="regular" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-[13.5px] font-medium text-slate-900 tracking-tight">{log.rule_label}</span>
                      <span className={`text-[10px] uppercase tracking-[0.14em] font-medium ${
                        log.decision === "acted" ? "text-emerald-700" :
                        log.decision === "cooldown" ? "text-amber-700" :
                        "text-slate-400"
                      }`}>
                        {log.decision === "acted" ? "Executou" :
                         log.decision === "cooldown" ? "Em cooldown" :
                         log.decision === "skipped" ? "Pulado" :
                         log.decision}
                      </span>
                    </div>
                    {log.reason && (
                      <div className="text-[12px] text-slate-500 mt-0.5">{humanizeReason(log.reason)}</div>
                    )}
                    <div className="text-[11px] text-slate-400 tabular-nums mt-0.5">
                      {log.created_at ? new Date(log.created_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* ─── Run result modal ─── */}
      {(running || runResult) && (
        <RunResultModal
          running={running}
          result={runResult}
          onClose={() => setRunResult(null)}
        />
      )}

      {/* ─── Edit drawer ─── */}
      {editing && (
        <EditAutomationDrawer
          rule={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {/* ─── Create-from-scratch modal ─── */}
      {creating && (
        <CreateAutomationModal
          existingCodes={rules.map((r) => r.code)}
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); }}
        />
      )}
    </>
  );
}


// ───────────────────────────────────────────────────────────────────────
// RunResultModal — shows progress + summary after a manual run.
// ───────────────────────────────────────────────────────────────────────

function RunResultModal({ running, result, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="run-result-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-7 relative">
        {!running && (
          <button
            onClick={onClose}
            data-testid="run-result-close"
            className="absolute top-4 right-4 p-1 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100"
          >
            <X size={14} />
          </button>
        )}
        {running ? (
          <div className="text-center py-4">
            <div className="w-14 h-14 mx-auto rounded-full bg-slate-900 flex items-center justify-center mb-4">
              <Spinner size={22} className="text-white animate-spin" />
            </div>
            <h3 className="text-[17px] font-semibold text-slate-900 tracking-tight">O AgentUS está trabalhando…</h3>
            <p className="text-[13px] text-slate-500 mt-1.5 leading-relaxed">
              {enablesKm
                ? "Analisando clientes, veículos e oportunidades. Aguarde alguns segundos."
                : "Analisando clientes e oportunidades. Aguarde alguns segundos."}
            </p>
          </div>
        ) : (
          <div>
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">
              <CheckCircle size={22} weight="fill" />
            </div>
            <h3 className="text-center text-[18px] font-semibold text-slate-900 tracking-tight">Análise concluída</h3>
            <p className="text-center text-[13.5px] text-slate-600 mt-2 leading-relaxed">
              {result?.skipped === "test_safe_mode" ? (
                <>O AgentUS está em <span className="font-medium">modo seguro</span>. Ative o modo produção em <span className="font-medium">Mensagens → Recepção</span> pra deixá-lo agir.</>
              ) : (
                <>
                  O AgentUS analisou{" "}
                  <span className="font-semibold text-slate-900 tabular-nums">{result?.customers_analyzed ?? 0} clientes</span>
                  {enablesKm && (result?.vehicles_analyzed ?? 0) > 0 && (
                    <> e <span className="font-semibold text-slate-900 tabular-nums">{result.vehicles_analyzed} veículos</span></>
                  )}
                  {" "}e identificou{" "}
                  <span className="font-semibold text-slate-900 tabular-nums">{result?.opportunities ?? 0} oportunidades</span> automáticas.
                </>
              )}
            </p>
            <div className="grid grid-cols-3 gap-3 mt-6">
              <Stat label="Clientes" value={result?.customers_analyzed ?? 0} />
              <Stat label="Ações executadas" value={result?.actions_fired ?? 0} accent />
              <Stat label="Mensagens enviadas" value={result?.messages_sent ?? 0} />
            </div>
            {result?.by_rule && Object.keys(result.by_rule).some((k) => result.by_rule[k] > 0) && (
              <div className="mt-5 pt-5 border-t border-slate-100">
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400 mb-2">Detalhe por automação</div>
                <ul className="text-[13px] text-slate-600 space-y-1">
                  {Object.entries(result.by_rule)
                    .filter(([, n]) => n > 0)
                    .map(([code, n]) => (
                      <li key={code} className="flex justify-between">
                        <span>{HUMAN_RULE_LABEL[code] || code}</span>
                        <span className="tabular-nums font-medium text-slate-900">{n}</span>
                      </li>
                    ))}
                </ul>
              </div>
            )}
            <button
              onClick={onClose}
              data-testid="run-result-ok"
              className="mt-6 w-full bg-slate-900 text-white py-2.5 rounded-lg text-[13px] font-medium hover:bg-slate-700 transition-colors"
            >
              Pronto
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function humanizeReason(reason) {
  if (!reason) return null;
  // Strip technical strings like "within_720h" → "aguardando N dias antes de repetir"
  const matchHours = /within_(\d+)h/.exec(reason);
  if (matchHours) {
    const h = parseInt(matchHours[1], 10);
    if (h >= 24) {
      const days = Math.round(h / 24);
      return `aguardando ${days} ${days === 1 ? "dia" : "dias"} antes de repetir`;
    }
    return `aguardando ${h}h antes de repetir`;
  }
  const map = {
    human_handoff_active: "Conversa com atendente humano",
    empty_template: "Modelo de mensagem vazio",
    test_safe_mode: "Modo seguro ativo",
  };
  return map[reason] || reason;
}

const HUMAN_RULE_LABEL = {
  reactivate_60d: "Cliente inativo",
  follow_up_no_reply_24h: "Cliente não respondeu",
  post_attended_thanks_24h: "Pós-atendimento",
  oil_change_due_by_km: "Troca de óleo próxima",
};


function Stat({ label, value, accent }) {
  return (
    <div className={`rounded-lg p-3 text-center ${accent ? "bg-slate-900 text-white" : "bg-slate-50 text-slate-700"}`}>
      <div className={`text-[20px] font-semibold tabular-nums tracking-tight ${accent ? "" : "text-slate-900"}`}>{value}</div>
      <div className={`text-[10.5px] uppercase tracking-[0.14em] mt-0.5 ${accent ? "text-slate-400" : "text-slate-500"}`}>{label}</div>
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────────
// EditAutomationDrawer — minimal, safe-by-design rule editor.
// Only exposes 4 fields per the product brief:
//   - active toggle
//   - "Quando" (cooldown_hours)
//   - "Quanto tempo o AgentUS aguarda antes de repetir" (cooldown)
//   - message_template
// Internal trigger_type / condition / action / priority stay hidden so
// the operator can't break the engine.
// ───────────────────────────────────────────────────────────────────────

function EditAutomationDrawer({ rule, onClose, onSaved }) {
  const [active, setActive] = useState(rule.active);
  const editTemplateRef = useRef(null);

  // ─── Delay-after-trigger (when the AgentUS reacts) ───────────────────
  // Stored inside `condition` under different keys depending on trigger:
  //   inactivity        → condition.days_inactive (DAYS)
  //   response_delay    → condition.hours_no_reply (HOURS)
  //   appointment_status→ condition.hours_after (HOURS)
  // The operator wants a unified UX: pick a NUMBER + a UNIT (minutos /
  // horas / dias) and we translate to the right key when saving.
  const triggerType = rule.trigger_type;
  const conditionKey =
    triggerType === "inactivity" ? "days_inactive" :
    triggerType === "response_delay" ? "hours_no_reply" :
    triggerType === "appointment_status" ? "hours_after" :
    null;
  const conditionDefaultUnit =
    triggerType === "inactivity" ? "dias" : "horas";
  const conditionRawValue = (rule.condition || {})[conditionKey];

  // We convert the stored value into the unit + amount the user sees.
  // Stored values are integers; for HOURS-based keys we let the user
  // pick MINUTES by storing < 1h as fractional (Math.round preserves
  // precision when saving back: e.g. 30min → hours_after = 0.5).
  const initWhenAmount = conditionRawValue ?? null;
  const initWhenUnit = conditionDefaultUnit;
  const [whenAmount, setWhenAmount] = useState(initWhenAmount ?? "");
  const [whenUnit, setWhenUnit] = useState(initWhenUnit);

  // ─── Cooldown (gap between two consecutive sends to same customer) ───
  // Now also editable in min/horas/dias.
  const initCooldownAmount = rule.cooldown_hours;
  const [cooldownAmount, setCooldownAmount] = useState(initCooldownAmount);
  const [cooldownUnit, setCooldownUnit] = useState("horas");

  const [template, setTemplate] = useState(rule.message_template || "");
  const [saving, setSaving] = useState(false);

  // Convert a (amount, unit) pair into the canonical storage unit for
  // this rule type. Storage unit is HOURS for hours_after/hours_no_reply
  // and DAYS for days_inactive. Returns an int (Mongo stores ints).
  const toStorageForCondition = (amount, unit) => {
    const n = Number(amount);
    if (!isFinite(n) || n <= 0) return null;
    if (conditionDefaultUnit === "dias") {
      // days_inactive is in DAYS. Allow only "dias" — anything smaller
      // doesn't really make business sense for a reactivation flow.
      return Math.max(1, Math.round(n));
    }
    // hours-based. Convert from chosen unit.
    let asHours;
    if (unit === "minutos") asHours = n / 60;
    else if (unit === "dias") asHours = n * 24;
    else asHours = n;
    // We need an int (Mongo). For sub-hour delays, round up to keep
    // the spirit of the operator's choice while staying integer-safe.
    return Math.max(1, Math.round(asHours));
  };

  const toStorageForCooldown = (amount, unit) => {
    const n = Number(amount);
    if (!isFinite(n) || n <= 0) return rule.cooldown_hours;
    if (unit === "minutos") return Math.max(1, Math.round(n / 60));
    if (unit === "dias") return Math.max(1, Math.round(n * 24));
    return Math.max(1, Math.round(n));
  };

  const save = async () => {
    setSaving(true);
    try {
      const nextCondition = { ...(rule.condition || {}) };
      if (conditionKey) {
        const stored = toStorageForCondition(whenAmount, whenUnit);
        if (stored != null) nextCondition[conditionKey] = stored;
      }
      await api.put(`/decision/rules/${rule.id}`, {
        code: rule.code,
        label: rule.label,
        trigger_type: rule.trigger_type,
        condition: nextCondition,
        action: rule.action,
        message_template: template,
        cooldown_hours: toStorageForCooldown(cooldownAmount, cooldownUnit),
        priority: rule.priority,
        active,
      });
      toast.success("Automação atualizada!");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-stretch justify-end" data-testid="edit-automation-drawer">
      <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl">
        <header className="px-6 py-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400 mb-1">Editar automação</div>
            <h2 className="text-[17px] font-semibold tracking-tight text-slate-900">{rule.label}</h2>
            <p className="text-[12px] text-slate-500 mt-0.5">{rule.when_human}</p>
          </div>
          <button onClick={onClose} data-testid="edit-close-btn" className="p-1.5 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100">
            <X size={16} />
          </button>
        </header>

        <div className="p-6 space-y-6">
          {/* Active toggle */}
          <div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[13px] font-medium text-slate-900">Automação ativa</div>
                <div className="text-[12px] text-slate-500 mt-0.5">Quando desativada, o AgentUS ignora essa regra.</div>
              </div>
              <button
                onClick={() => setActive(!active)}
                data-testid="edit-toggle"
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${active ? "bg-emerald-500" : "bg-slate-300"}`}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${active ? "translate-x-5" : "translate-x-1"}`} />
              </button>
            </div>
          </div>

          {/* "Quando enviar" — delay between trigger event and message */}
          {conditionKey && (
            <label className="block" data-testid="edit-when-block">
              <div className="text-[13px] font-medium text-slate-900">Quando enviar</div>
              <div className="text-[12px] text-slate-500 mt-0.5 mb-2">
                {triggerType === "inactivity" && "Tempo sem interação do cliente antes de tentar reativar."}
                {triggerType === "response_delay" && "Tempo sem resposta do cliente antes de mandar follow-up."}
                {triggerType === "appointment_status" && "Tempo após o atendimento antes de enviar a mensagem."}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  value={whenAmount}
                  onChange={(e) => setWhenAmount(e.target.value)}
                  data-testid="edit-when-amount"
                  className="w-24 rounded-md border border-slate-300 px-3 py-2 text-sm tabular-nums"
                />
                <select
                  value={whenUnit}
                  onChange={(e) => setWhenUnit(e.target.value)}
                  data-testid="edit-when-unit"
                  className="rounded-md border border-slate-300 px-2.5 py-2 text-sm bg-white"
                  disabled={conditionDefaultUnit === "dias"}
                >
                  {conditionDefaultUnit === "dias" ? (
                    <option value="dias">dias</option>
                  ) : (
                    <>
                      <option value="minutos">minutos</option>
                      <option value="horas">horas</option>
                      <option value="dias">dias</option>
                    </>
                  )}
                </select>
              </div>
              {conditionDefaultUnit !== "dias" && Number(whenAmount) > 0 && whenUnit === "minutos" && Number(whenAmount) < 60 && (
                <div className="text-[11px] text-amber-700 mt-1.5">
                  Atenção: valores abaixo de 1 hora são arredondados para 1h no envio (limitação do agendador).
                </div>
              )}
            </label>
          )}

          {/* Cooldown */}
          <label className="block">
            <div className="text-[13px] font-medium text-slate-900">Aguardar antes de repetir</div>
            <div className="text-[12px] text-slate-500 mt-0.5 mb-2">Tempo mínimo entre 2 ações da mesma automação pro mesmo cliente.</div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                value={cooldownAmount}
                onChange={(e) => setCooldownAmount(e.target.value)}
                data-testid="edit-cooldown"
                className="w-24 rounded-md border border-slate-300 px-3 py-2 text-sm tabular-nums"
              />
              <select
                value={cooldownUnit}
                onChange={(e) => setCooldownUnit(e.target.value)}
                data-testid="edit-cooldown-unit"
                className="rounded-md border border-slate-300 px-2.5 py-2 text-sm bg-white"
              >
                <option value="minutos">minutos</option>
                <option value="horas">horas</option>
                <option value="dias">dias</option>
              </select>
            </div>
          </label>

          {/* Message template */}
          <label className="block">
            <div className="text-[13px] font-medium text-slate-900">Mensagem enviada</div>
            <div className="text-[12px] text-slate-500 mt-0.5 mb-2">
              Personalize com as variáveis abaixo — o AgentUS substitui pelos dados reais do cliente.
            </div>
            <textarea
              ref={editTemplateRef}
              rows={6}
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              data-testid="edit-template"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm leading-relaxed font-mono"
            />
            <div className="mt-1.5">
              <VariableInserter
                textareaRef={editTemplateRef}
                value={template}
                onChange={setTemplate}
                variables={RULE_VARS}
              />
            </div>
          </label>

          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={save}
              disabled={saving}
              data-testid="edit-save-btn"
              className="flex-1 bg-slate-900 text-white py-2.5 rounded-lg text-[13px] font-medium hover:bg-slate-700 transition-colors disabled:opacity-60"
            >
              {saving ? "Salvando…" : "Salvar"}
            </button>
            <button
              onClick={onClose}
              className="text-[13px] text-slate-500 hover:text-slate-900 px-3 py-2.5"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────────
// CreateAutomationModal — constrained custom-rule builder.
//
// Why constrained?
//   The Decision Engine only knows how to evaluate 4 trigger types
//   (inactivity, response_delay, appointment_status, vehicle_km). If we
//   let the operator type a free-form trigger the IA will never execute
//   the rule. Instead we expose 3 well-tested presets behind plain-PT
//   wording and let the operator pick the parameter (days / hours) +
//   write the message they want the AgentUS to send.
// ───────────────────────────────────────────────────────────────────────

const TRIGGER_PRESETS = [
  {
    id: "inactivity_days",
    title: "Cliente parado há X dias",
    desc: "Reativa quem não interage com você há um tempo.",
    trigger_type: "inactivity",
    paramKey: "days_inactive",
    paramLabel: "Dias sem interagir",
    paramUnit: "dias",
    paramDefault: 60,
    paramMin: 7,
    paramMax: 365,
    cooldownDefaultHours: 24 * 30,
    sampleTemplate:
      "Oi {nome_cliente}! Faz um tempinho que não te vejo por aqui. " +
      "Posso te ajudar com um agendamento?",
  },
  {
    id: "no_reply_hours",
    title: "Cliente não respondeu há X horas",
    desc: "Faz follow-up quando alguém viu a mensagem mas não respondeu.",
    trigger_type: "response_delay",
    paramKey: "hours_no_reply",
    paramLabel: "Horas sem resposta",
    paramUnit: "horas",
    paramDefault: 24,
    paramMin: 1,
    paramMax: 168,
    cooldownDefaultHours: 48,
    sampleTemplate:
      "Oi {nome_cliente}, tudo bem? Mandei uma mensagem e fiquei sem retorno. " +
      "Posso te ajudar com alguma coisa?",
  },
  {
    id: "after_attended_hours",
    title: "X horas após o atendimento",
    desc: "Acompanha o cliente depois que ele foi atendido.",
    trigger_type: "appointment_status",
    paramKey: "hours_after",
    paramLabel: "Horas após o atendimento",
    paramUnit: "horas",
    paramDefault: 24,
    paramMin: 1,
    paramMax: 240,
    extraCondition: { status: "attended" },
    cooldownDefaultHours: 24 * 14,
    sampleTemplate:
      "Oi {nome_cliente}, espero que esteja tudo certo! " +
      "Qualquer coisa, é só me chamar.",
  },
];

function slugify(s) {
  return (s || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);
}

function CreateAutomationModal({ existingCodes, onClose, onCreated }) {
  const [step, setStep] = useState(1);   // 1 = pick preset, 2 = customize
  const [preset, setPreset] = useState(null);
  const [label, setLabel] = useState("");
  const [param, setParam] = useState(0);
  const [cooldownDays, setCooldownDays] = useState(7);
  const [template, setTemplate] = useState("");
  const [activate, setActivate] = useState(true);
  const [saving, setSaving] = useState(false);
  const [labelErr, setLabelErr] = useState("");
  const [templateErr, setTemplateErr] = useState("");
  const createTemplateRef = useRef(null);

  const pickPreset = (p) => {
    setPreset(p);
    setLabel(p.title);
    setParam(p.paramDefault);
    setCooldownDays(Math.max(1, Math.round((p.cooldownDefaultHours || 168) / 24)));
    setTemplate(p.sampleTemplate);
    setStep(2);
  };

  const save = async () => {
    setLabelErr("");
    setTemplateErr("");
    const trimmedLabel = (label || "").trim();
    const trimmedTemplate = (template || "").trim();
    if (trimmedLabel.length < 3) {
      setLabelErr("Dê um nome curto e claro (mínimo 3 letras).");
      return;
    }
    if (trimmedTemplate.length < 10) {
      setTemplateErr("Escreva a mensagem que o AgentUS vai enviar.");
      return;
    }
    const numericParam = parseInt(param, 10);
    if (
      isNaN(numericParam)
      || numericParam < preset.paramMin
      || numericParam > preset.paramMax
    ) {
      toast.error(`O valor deve ficar entre ${preset.paramMin} e ${preset.paramMax} ${preset.paramUnit}.`);
      return;
    }
    // Build a unique code from the label slug
    let baseCode = `custom_${slugify(trimmedLabel)}` || `custom_${preset.id}`;
    let code = baseCode;
    let i = 2;
    while (existingCodes.includes(code)) {
      code = `${baseCode}_${i++}`;
    }
    const condition = { [preset.paramKey]: numericParam, ...(preset.extraCondition || {}) };
    const cooldownHours = Math.max(1, parseInt(cooldownDays, 10) || 7) * 24;

    setSaving(true);
    try {
      await api.post("/decision/rules", {
        code,
        label: trimmedLabel,
        trigger_type: preset.trigger_type,
        condition,
        action: "send_message",
        message_template: trimmedTemplate,
        cooldown_hours: cooldownHours,
        priority: 50,
        active: activate,
      });
      toast.success(activate
        ? "Automação criada e ativada — o AgentUS já vai começar a usar."
        : "Automação criada. Você pode ativar quando quiser.");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não consegui criar a automação.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="create-automation-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto">
        <header className="px-6 py-4 border-b border-slate-200 flex items-start justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400 mb-1">
              {step === 1 ? "Passo 1 de 2" : "Passo 2 de 2"}
            </div>
            <h2 className="text-[17px] font-semibold tracking-tight text-slate-900">
              {step === 1 ? "Quando o AgentUS deve agir?" : "Personalize sua automação"}
            </h2>
            <p className="text-[12px] text-slate-500 mt-0.5">
              {step === 1
                ? "Escolha um gatilho que o AgentUS sabe monitorar sozinho."
                : "Defina o intervalo e a mensagem. O AgentUS faz o resto."}
            </p>
          </div>
          <button onClick={onClose} data-testid="create-close-btn" className="p-1.5 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100">
            <X size={16} />
          </button>
        </header>

        {step === 1 && (
          <div className="p-6 space-y-3" data-testid="create-step-1">
            {TRIGGER_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => pickPreset(p)}
                data-testid={`create-preset-${p.id}`}
                className="w-full text-left rounded-xl border border-slate-200 hover:border-slate-900 hover:bg-slate-50 transition-colors p-4"
              >
                <div className="text-[14px] font-semibold text-slate-900 mb-0.5">{p.title}</div>
                <div className="text-[12.5px] text-slate-500 leading-relaxed">{p.desc}</div>
              </button>
            ))}
            <div className="text-[11.5px] text-slate-500 italic pt-2 leading-relaxed">
              Esses são os únicos gatilhos que o AgentUS consegue monitorar automaticamente hoje. Se você precisar de algo diferente, fale com o suporte que avaliamos a viabilidade.
            </div>
          </div>
        )}

        {step === 2 && preset && (
          <div className="p-6 space-y-5" data-testid="create-step-2">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-[12px] text-slate-600 flex items-center gap-2">
              <Sparkle size={13} weight="fill" className="text-slate-400 shrink-0" />
              <span>Gatilho escolhido: <strong className="text-slate-900">{preset.title}</strong></span>
              <button type="button" onClick={() => setStep(1)} className="ml-auto text-[11.5px] text-slate-500 underline hover:text-slate-900">trocar</button>
            </div>

            {/* Label */}
            <label className="block">
              <div className="text-[13px] font-medium text-slate-900">Nome da automação</div>
              <div className="text-[12px] text-slate-500 mt-0.5 mb-2">Aparece pra você na lista, não pro cliente.</div>
              <input
                type="text"
                value={label}
                maxLength={60}
                onChange={(e) => { setLabel(e.target.value); setLabelErr(""); }}
                data-testid="create-label-input"
                placeholder="Ex.: Reativar clientes parados 90 dias"
                className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${labelErr ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-slate-300 focus:border-slate-900 focus:ring-slate-900"}`}
              />
              {labelErr && <p className="text-[11.5px] text-red-600 mt-1">{labelErr}</p>}
            </label>

            {/* Param */}
            <label className="block">
              <div className="text-[13px] font-medium text-slate-900">{preset.paramLabel}</div>
              <div className="text-[12px] text-slate-500 mt-0.5 mb-2">
                Entre {preset.paramMin} e {preset.paramMax} {preset.paramUnit}.
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={preset.paramMin}
                  max={preset.paramMax}
                  value={param}
                  onChange={(e) => setParam(e.target.value)}
                  data-testid="create-param-input"
                  className="w-28 rounded-md border border-slate-300 px-3 py-2 text-sm tabular-nums focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                />
                <span className="text-[13px] text-slate-600">{preset.paramUnit}</span>
              </div>
            </label>

            {/* Cooldown */}
            <label className="block">
              <div className="text-[13px] font-medium text-slate-900">Aguardar antes de repetir</div>
              <div className="text-[12px] text-slate-500 mt-0.5 mb-2">Tempo mínimo entre 2 disparos da mesma automação pro mesmo cliente.</div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={cooldownDays}
                  onChange={(e) => setCooldownDays(e.target.value)}
                  data-testid="create-cooldown-input"
                  className="w-28 rounded-md border border-slate-300 px-3 py-2 text-sm tabular-nums focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                />
                <span className="text-[13px] text-slate-600">dias</span>
              </div>
            </label>

            {/* Template */}
            <label className="block">
              <div className="text-[13px] font-medium text-slate-900">Mensagem enviada</div>
              <div className="text-[12px] text-slate-500 mt-0.5 mb-2">
                Personalize com as variáveis abaixo — o AgentUS substitui pelos dados reais do cliente.
              </div>
              <textarea
                ref={createTemplateRef}
                rows={5}
                value={template}
                onChange={(e) => { setTemplate(e.target.value); setTemplateErr(""); }}
                data-testid="create-template-input"
                className={`w-full rounded-md border px-3 py-2 text-sm leading-relaxed font-mono focus:outline-none focus:ring-1 ${templateErr ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-slate-300 focus:border-slate-900 focus:ring-slate-900"}`}
              />
              <div className="mt-1.5">
                <VariableInserter
                  textareaRef={createTemplateRef}
                  value={template}
                  onChange={(v) => { setTemplate(v); setTemplateErr(""); }}
                  variables={RULE_VARS}
                />
              </div>
              {templateErr && <p className="text-[11.5px] text-red-600 mt-1">{templateErr}</p>}
            </label>

            {/* Activate */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={activate}
                onChange={(e) => setActivate(e.target.checked)}
                data-testid="create-activate-toggle"
                className="mt-0.5"
              />
              <div>
                <div className="text-[13px] font-medium text-slate-900">Já ativar essa automação</div>
                <div className="text-[12px] text-slate-500">Você pode desativar depois com 1 clique na lista.</div>
              </div>
            </label>

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={save}
                disabled={saving}
                data-testid="create-save-btn"
                className="flex-1 bg-slate-900 text-white py-2.5 rounded-lg text-[13px] font-medium hover:bg-slate-700 transition-colors disabled:opacity-60"
              >
                {saving ? "Criando…" : "Criar automação"}
              </button>
              <button
                onClick={onClose}
                className="text-[13px] text-slate-500 hover:text-slate-900 px-3 py-2.5"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
